import axios from 'axios';

/**
 * Whether a road-quality segment lies on the drawn route: its start or end
 * within `maxMiles` of the route line. Checks every leg of the route, so a
 * long trip with sparse points doesn't skip the stretches between them.
 * @param {Object} seg - { startLat, startLng, endLat, endLng }
 * @param {Array} routeCoords - [lat, lng] pairs or { lat, lng } objects
 */
export function isSegmentOnRoute(seg, routeCoords, maxMiles = 0.5) {
    if (!routeCoords || routeCoords.length === 0) return true;
    const near = (lat, lng) => {
        if (!lat || !lng) return false;
        const milesPerDegLng = 69.0 * Math.cos(lat * Math.PI / 180);
        const pad = maxMiles / 69.0;
        for (let i = 0; i < routeCoords.length; i++) {
            const a = routeCoords[i], b = routeCoords[Math.min(i + 1, routeCoords.length - 1)];
            const aLat = a.lat ?? a[0], aLng = a.lng ?? a[1], bLat = b.lat ?? b[0], bLng = b.lng ?? b[1];
            if (lat < Math.min(aLat, bLat) - pad || lat > Math.max(aLat, bLat) + pad) continue;
            const ax = (aLng - lng) * milesPerDegLng, ay = (aLat - lat) * 69.0;
            const dx = (bLng - aLng) * milesPerDegLng, dy = (bLat - aLat) * 69.0;
            const lenSq = dx * dx + dy * dy;
            const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lenSq));
            if (Math.hypot(ax + t * dx, ay + t * dy) <= maxMiles) return true;
        }
        return false;
    };
    return near(seg.startLat, seg.startLng) || near(seg.endLat, seg.endLng);
}

/**
 * RoadQualityService — queries road quality data along a route
 * 
 * Uses HPMS IRI (International Roughness Index) data from the backend.
 * Free users get summary + warnings. Pro users get full segment breakdown.
 * 
 * IRI Score Reference:
 *   < 95    → Excellent (smooth highway)
 *   95–170  → Good (minor roughness)
 *   170–220 → Fair (noticeable roughness)
 *   > 220   → Poor (rough — use caution with trailer)
 */
export class RoadQualityService {
    constructor() {
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        this.useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
    }

    /**
     * Get road quality data for a route.
     * @param {Array} waypoints - Array of {lat, lng} objects along the route
     * @param {string} plan - User plan: 'free', 'subscriber', 'founding_member', 'lifetime'
     * @returns {Object} { summary, warnings, segments (pro only), proPreview (free only) }
     */
    async getRoadQualityForRoute(waypoints, plan = 'free', highways = []) {
        if (!this.useBackend || !this.backendUrl) {
            console.log('⚠️ Road quality: backend not configured');
            return this.getEmptyResult();
        }

        if (!waypoints || waypoints.length < 2) {
            return this.getEmptyResult();
        }

        // Sample waypoints to reduce payload — every 5th point, max 30
        const sampled = this.sampleWaypoints(waypoints, 30);

        try {
            // The backend reads the plan from the account when signed in;
            // without a token it answers with the free view.
            const headers = {};
            try {
                await window.authService?.refreshIfNeeded?.();
                const token = window.authService?.getAccessToken?.();
                if (token) headers.Authorization = `Bearer ${token}`;
            } catch (e) { /* signed out: free view */ }

            const response = await axios.post(`${this.backendUrl}/api/road-quality`, {
                waypoints: sampled,
                plan,
                highways: highways || []
            }, { timeout: 60000, headers });

            if (response.data && response.data.status === 'success') {
                console.log(`✓ Road quality: ${response.data.segmentCount} segments, overall: ${response.data.summary?.overall}`);
                return response.data;
            }

            return this.getEmptyResult();
        } catch (error) {
            console.warn('Road quality fetch failed:', error.message);
            return this.getEmptyResult();
        }
    }

    /**
     * Check if a route has any road quality warnings.
     * Quick check for the route summary card — doesn't need full segment data.
     */
    hasWarnings(roadQualityData) {
        if (!roadQualityData || !roadQualityData.warnings) return false;
        return roadQualityData.warnings.length > 0;
    }

    /**
     * Get the severity color for an IRI rating.
     */
    getRatingColor(rating) {
        const colors = {
            excellent: '#22c55e', // green
            good: '#22c55e',     // green
            fair: '#eab308',     // yellow
            poor: '#ef4444',     // red
            unknown: '#6b7280'   // gray
        };
        return colors[rating] || colors.unknown;
    }

    /**
     * Get a human-readable label for an IRI rating.
     */
    getRatingLabel(rating) {
        const labels = {
            excellent: 'Smooth',
            good: 'Good',
            fair: 'Fair',
            poor: 'Rough',
            unknown: 'No data'
        };
        return labels[rating] || labels.unknown;
    }

    /**
     * Get the map overlay color for a segment based on IRI score.
     * Used for the Pro color-coded route line.
     */
    getSegmentColor(iriScore) {
        if (!iriScore || iriScore <= 0) return '#6b7280'; // gray — no data
        if (iriScore < 170) return '#22c55e';  // green — good
        if (iriScore < 220) return '#eab308';  // yellow — fair
        return '#ef4444';                       // red — poor
    }

    /**
     * Sample waypoints evenly to reduce API payload.
     */
    sampleWaypoints(waypoints, maxPoints) {
        if (waypoints.length <= maxPoints) return waypoints;

        const sampled = [];
        const step = (waypoints.length - 1) / (maxPoints - 1);

        for (let i = 0; i < maxPoints; i++) {
            const index = Math.round(i * step);
            sampled.push(waypoints[index]);
        }

        return sampled;
    }

    /**
     * Return empty result when data is unavailable.
     */
    getEmptyResult() {
        return {
            status: 'success',
            summary: { overall: 'unknown', roughSegments: 0, unpavedSegments: 0, totalSegments: 0 },
            warnings: [],
            segments: [],
            segmentCount: 0
        };
    }
}
